import React, { useMemo, useState } from 'react';
import { Table, Whisper, Popover } from 'rsuite';
import TableSkeleton from '@/common/view/TableSkeleton';
import { isWithinInterval, parseISO, startOfDay, endOfDay, subYears } from 'date-fns';

import { useCustomerDetailPageContext } from '../datacontroller/CustomerDetailPageContext';
import { toDateText } from '@/common/util/formatUtil';
import DateRangePickerButton from '@/common/view/date-range-picker/button';
import Icon from '@/common/view/icons';
import Button from '@/common/view/button';
import Text from '@/common/view/Text';
import CampaignApiAgent from '@/campaign/main/agent/CampaignApiAgent';

const { Column, HeaderCell, Cell } = Table;

const DateCell = ({ rowData, dataKey, ...props }: any) => {
  const dateValue = rowData[dataKey];
  if (!dateValue) {
    return <Cell {...props}>-</Cell>;
  }
  try {
    return <Cell {...props}>{toDateText(new Date(dateValue), 'yyyy-MM-dd')}</Cell>;
  } catch {
    return <Cell {...props}>-</Cell>;
  }
};

const ResponseCell = ({ rowData, dataKey, ...props }: any) => {
  const value = rowData[dataKey];
  return <Cell {...props}>{value === 'y' ? '구매' : '미구매'}</Cell>;
};

const MessageCell = ({ rowData, dataKey, ...props }: any) => {
  const message = rowData[dataKey];
  if (!message) {
    return <Cell {...props}>-</Cell>;
  }

  return (
    <Cell {...props}>
      <Whisper
        placement="topEnd"
        trigger="hover"
        enterable
        preventOverflow
        speaker={
          <Popover style={{ maxWidth: '400px', wordBreak: 'break-word' }}>
            <Text typo="body4" color="grayBlack" className="cursor-default">
              {message}
            </Text>
          </Popover>
        }>
        <span
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'default',
          }}>
          {message}
        </span>
      </Whisper>
    </Cell>
  );
};

const getDefaultDateRange = (): [Date, Date] => {
  const today = new Date();
  const oneYearAgo = subYears(today, 1);
  return [oneYearAgo, today];
};

export const CustomerCampaignMessageTable: React.FC = () => {
  const { customerId } = useCustomerDetailPageContext();
  const [dates, setDates] = useState<[Date, Date]>(getDefaultDateRange);
  const { data: messageHistory, isLoading } = CampaignApiAgent.useGetCustomerCampaignMessageHistory(customerId);

  const filteredMessageHistory = useMemo(() => {
    if (!messageHistory) return [];
    if (!dates) return messageHistory;

    const [startDate, endDate] = dates;
    return messageHistory.filter((message) => {
      try {
        const sendDate = parseISO(message.send_date);
        return isWithinInterval(sendDate, {
          start: startOfDay(startDate),
          end: endOfDay(endDate),
        });
      } catch {
        return false;
      }
    });
  }, [messageHistory, dates]);

  return (
    <>
      <div className="relative inline-flex gap-2">
        <DateRangePickerButton
          Icon={Icon.Calendar}
          value={dates}
          placement="topStart"
          placeholder="연.월.일 ~ 연.월.일"
          onChange={(dates) => {
            if (!dates) return;
            setDates(dates);
          }}
        />
        {dates && (
          <Button
            skin="gray-outlined"
            size="small"
            onClick={() => {
              setDates(getDefaultDateRange());
            }}>
            초기화
          </Button>
        )}
      </div>

      {isLoading ? (
        <TableSkeleton columns={7} rows={5} columnWidths={[1, 1, 1.5, 1, 1.5, 1, 3]} />
      ) : (
        <Table
          height={272}
          data={filteredMessageHistory}
          rowClassName="!cursor-default"
          locale={{
            loading: '로딩 중...',
            emptyMessage: '캠페인 메시지 발송 이력이 없습니다.',
          }}>
          <Column width={120} align="center">
            <HeaderCell>발송일자</HeaderCell>
            <DateCell dataKey="send_date" />
          </Column>
          <Column width={120} align="center">
            <HeaderCell>캠페인 ID</HeaderCell>
            <Cell dataKey="campaign_id" />
          </Column>
          <Column width={180} align="center">
            <HeaderCell>캠페인 이름</HeaderCell>
            <Cell dataKey="campaign_name" />
          </Column>
          <Column width={140} align="center">
            <HeaderCell>오디언스 ID</HeaderCell>
            <Cell dataKey="audience_id" />
          </Column>
          <Column width={180} align="center">
            <HeaderCell>오디언스 이름</HeaderCell>
            <Cell dataKey="audience_name" />
          </Column>
          <Column width={100} align="center">
            <HeaderCell>구매 여부</HeaderCell>
            <ResponseCell dataKey="is_purchase" />
          </Column>
          <Column flexGrow={1}>
            <HeaderCell align="center">메시지</HeaderCell>
            <MessageCell dataKey="send_message_body" />
          </Column>
        </Table>
      )}
    </>
  );
};
